import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ServiceAddress, ServiceDetails } from 'app/interfaces/journey-management/journey-management-object.interface';
import * as moment from 'moment';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import * as short from 'short-uuid';

@Component({
    selector: 'service-dialog.component',
    templateUrl: './service-dialog.component.html',
    styleUrls: [
        './service-dialog.component.css'
    ],
    encapsulation: ViewEncapsulation.None,
})
export class ServiceDialogComponent {
    dialogTitle: string;
    serviceInfoForm: FormGroup;
    resourceRoles: string[] = [];
    serviceDetailsArray = Object.values(ServiceDetails);
    minDate = moment().toDate();

    constructor(public dialogRef: MatDialogRef<ServiceDialogComponent>, private matSnackBar: MatSnackBar, private formBuilder: FormBuilder, @Inject(MAT_DIALOG_DATA) public data?: any) {
        this.dialogTitle = data.service ? 'Update Service Information' : 'Add Service Information';
        this.serviceInfoForm = this.formBuilder.group({
            key: [short.generate()],
            date: [moment(), Validators.required],
            timeStart: [''],
            startAddress: this.formBuilder.group({
                latitude: [null, Validators.required],
                longitude: [null, Validators.required],
                formattedAddress: [null, Validators.required]
            }),
            endAddress: this.formBuilder.group({
                latitude: [null, Validators.required],
                longitude: [null, Validators.required],
                formattedAddress: [null, Validators.required]
            }),
            detailsOfService: ['', Validators.required],
            specialRequests: [''],
            requiredResources: this.formBuilder.array([]),
            requiredResourceCount: [0],
            requiredAssetCount: [0],
            isProtocolRequired: [false],
            isArranged: [false],
            isCompleted: [false],
            linkedPassengers: [[]],
        });

        if (this.data.service) {
            this.serviceInfoForm.patchValue(this.data.service);
        }

        if (this.data.serviceDetails) {
            this.serviceDetailsArray = this.data.serviceDetails;
        } 

        if (this.data.resourceRoles) {
            this.resourceRoles = this.data.resourceRoles;
        }
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSubmit(): void {
        if (this.serviceInfoForm.valid) {
            const formValue = this.serviceInfoForm.getRawValue();

            if (formValue.requiredResourceCount === 0 || formValue.requiredResourceCount === null) {
                delete formValue.requiredResourceCount;
            }

            if (formValue.requiredAssetCount === 0 || formValue.requiredAssetCount === null) {
                delete formValue.requiredAssetCount;
            }

            this.serviceInfoForm.patchValue(formValue);
            this.dialogRef.close(this.serviceInfoForm);
        } 
        else {
            this.serviceInfoForm.markAllAsTouched();
            this.openSnackBar('Required fields missing');
        }
    }

    private openSnackBar(message: string, action?: string) {
        this.matSnackBar.open(message, action, {
            duration: 2000,
        });
    }

    onProtocolRequiredChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.serviceInfoForm.get('isProtocolRequired').setValue(true);
        }
        else {
            this.serviceInfoForm.get('isProtocolRequired').setValue(false);
        }
    }

    isResourceRequested(role: string): boolean {
        const resourcesArray = this.serviceInfoForm.get('requiredResources') as FormArray;

        return resourcesArray.controls.some(control => control.value === role);
    }

    toggleResourceRequest(role: string): void {
        const resourcesArray = this.serviceInfoForm.get('requiredResources') as FormArray;

        const index = resourcesArray.controls.findIndex(control => control.value === role);

        if (index === -1) {
            resourcesArray.push(this.formBuilder.control(role));
        }
        else {
            resourcesArray.removeAt(index);
        }
    }

    isCompletedOrActioned(): boolean {
        if (this.serviceInfoForm.get('isCompleted').value === true) {
            return true;
        }
        else {
            if (this.data.journey && this.data.journey.serviceInformation) {
                const service = this.data.journey.serviceInformation.find(service => this.serviceInfoForm.get('key').value === service.key);

                if (service) {
                    if (service.linkedResponseServiceKey) {
                        return true;
                    }
                    else {
                        return false;
                    }
                }
                else {
                    return false;
                }
            }
            else {
                return false;
            }
        }
    }

    handleAddressChange(googleAddress: Address, addressType: 'startAddress' | 'endAddress'): void {
        const location: ServiceAddress = {
            longitude: googleAddress.geometry.location.lng(),
            latitude: googleAddress.geometry.location.lat(),
            formattedAddress: `${googleAddress.name} - ${googleAddress.formatted_address}`
        };

        this.serviceInfoForm.get(addressType).setValue(location);
    }

    onServiceDateChange(event: any) {
        const timeStartCtrl = this.serviceInfoForm.get('timeStart');

        if (timeStartCtrl.valid && timeStartCtrl.dirty) {
            const hoursMinutes = timeStartCtrl.value.split(':');

            const currentDate = moment(event.value);

            currentDate.set({ hour: parseInt(hoursMinutes[0]), minute: parseInt(hoursMinutes[1]) });

            this.serviceInfoForm.get('date').setValue(currentDate.toDate(), { emitEvent: false });
        }
        else {
            this.serviceInfoForm.get('date').setValue(event.value, { emitEvent: false });
        }
    }

    onServiceTimeChange(event: any) {
        const timeInput = event.target.value;
        const hoursMinutes = timeInput.split(':');

        this.serviceInfoForm.get('timeStart').setValue(event.target.value, { emitEvent: false });

        if (this.serviceInfoForm.get('date').valid) {
            const currentDate = moment(this.serviceInfoForm.get('date').value);

            currentDate.set({ hour: parseInt(hoursMinutes[0]), minute: parseInt(hoursMinutes[1]) });

            this.serviceInfoForm.get('date').setValue(currentDate.toDate(), { emitEvent: false });
        }
    }

}